import {getPublicMessages, sendChatMessage} from '../api/';
import {useMutation, useQuery} from 'react-query';
import {
  CommonActions,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {setPaginationState} from '../store/slices/chat';
import {ChatParams} from '../types/nav';

export const useMessages = (chatId: string) => {
  const isLoadingEarlier = useAppSelector(
    state => state.chat.loadingPagination[chatId],
  );
  const dispatch = useAppDispatch();

  const route = useRoute();
  const focused = useIsFocused();
  const navigation = useNavigation();
  const params = route.params as ChatParams;
  const mnemonic = params?.mnemonic;
  const pageIndex = params?.page || 1;
  const perPage = 25;
  const messagesQuery = useQuery(
    'chatMessages_' + chatId,
    async () => {
      const [messages] = await Promise.all([
        getPublicMessages(chatId, mnemonic, perPage * pageIndex, 0),
      ]);
      return {messages};
    },
    {
      retry: true,
      refetchInterval: 10000,
      enabled: focused,
    },
  );
  const loadingMessages =
    messagesQuery.isFetching || isLoadingEarlier;

  const sendMessageMutation = useMutation(
    async (text: string) => {
      try {
        const message = await sendChatMessage(chatId, text, mnemonic);
        await messagesQuery.refetch();
        return message;
      } catch (err) {
        console.error('==== sendMessage error ===', err);
      }
    },
    {retry: true},
  );

  const messages = messagesQuery.data?.messages || [];

  const nextPage = async () => {
    if (isLoadingEarlier) return;

    const nPage = parseInt(pageIndex.toString(), 10) + 1;
    console.info('nextPage', nPage, loadingMessages);
    dispatch(setPaginationState({[chatId]: true}));
    navigation.dispatch({
      ...CommonActions.setParams({page: nPage}),
      source: route.key,
    });
    await messagesQuery.refetch();
    setTimeout(() => {
      dispatch(setPaginationState({[chatId]: false}));
    }, 2000);
  };

  return {
    messages,
    nextPage,
    loadingMessages,
    sendingMessage: sendMessageMutation.isLoading,
    messagesError: messagesQuery.error as string | undefined,
    sendMessage: sendMessageMutation.mutate,
    isLoadingEarlier,
  };
};
